export type CampaignAttribution = {
  campaign_source?: string;
  campaign_medium?: string;
  campaign_name?: string;
  campaign_content?: string;
  campaign_term?: string;
};

type StoredCampaignAttribution = {
  attribution: CampaignAttribution;
  expiresAt: number;
};

const STORAGE_KEY = "artify_campaign_attribution";
const ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_VALUE_LENGTH = 64;

const UTM_TO_ATTRIBUTION_KEY = {
  utm_source: "campaign_source",
  utm_medium: "campaign_medium",
  utm_campaign: "campaign_name",
  utm_content: "campaign_content",
  utm_term: "campaign_term",
} as const;

const ALLOWED_VALUE_PATTERN = /^[a-z0-9_-]+$/;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
const URL_PATTERN = /(?:https?:\/\/|www\.|[a-z0-9-]+\.[a-z]{2,}\/)/i;
const TOKENISH_PATTERN = /(?:token|secret|password|passwd|pwd|key|jwt|bearer|stripe|supabase|session|auth)/i;

function looksLikePhoneNumber(value: string): boolean {
  const digitCount = value.replace(/\D/g, "").length;
  return value.startsWith("+") || digitCount >= 8;
}

function isBrowserStorageAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

export function sanitizeCampaignValue(value: string | null): string | undefined {
  if (!value) return undefined;

  const trimmed = value.trim().toLowerCase();
  if (!trimmed || trimmed.length > MAX_VALUE_LENGTH) return undefined;
  if (!ALLOWED_VALUE_PATTERN.test(trimmed)) return undefined;
  if (EMAIL_PATTERN.test(trimmed) || URL_PATTERN.test(trimmed) || looksLikePhoneNumber(trimmed) || TOKENISH_PATTERN.test(trimmed)) {
    return undefined;
  }

  return trimmed;
}

function readStoredAttribution(now = Date.now()): CampaignAttribution {
  if (!isBrowserStorageAvailable()) return {};

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    const stored = JSON.parse(raw) as Partial<StoredCampaignAttribution>;
    if (!stored.expiresAt || stored.expiresAt <= now) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return {};
    }

    return sanitizeAttribution(stored.attribution ?? {});
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY);
    return {};
  }
}

function sanitizeAttribution(attribution: Partial<CampaignAttribution>): CampaignAttribution {
  return Object.fromEntries(
    Object.entries(attribution)
      .map(([key, value]) => [key, typeof value === "string" ? sanitizeCampaignValue(value) : undefined] as const)
      .filter(([, value]) => value !== undefined),
  ) as CampaignAttribution;
}

function persistAttribution(attribution: CampaignAttribution, now = Date.now()) {
  if (!isBrowserStorageAvailable() || Object.keys(attribution).length === 0) return;

  const stored: StoredCampaignAttribution = {
    attribution,
    expiresAt: now + ATTRIBUTION_TTL_MS,
  };

  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

export function initializeCampaignAttribution(search = typeof window !== "undefined" ? window.location.search : "") {
  if (!isBrowserStorageAvailable()) return;

  const params = new URLSearchParams(search);
  const attribution = Object.fromEntries(
    Object.entries(UTM_TO_ATTRIBUTION_KEY)
      .map(([utmKey, attributionKey]) => [attributionKey, sanitizeCampaignValue(params.get(utmKey))] as const)
      .filter(([, value]) => value !== undefined),
  ) as CampaignAttribution;

  if (Object.keys(attribution).length > 0) {
    persistAttribution(attribution);
  } else {
    readStoredAttribution();
  }
}

export function getCampaignAttribution(): CampaignAttribution {
  return readStoredAttribution();
}
